import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_DUMMY_ROOM_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type ProductDummyRoomFormModel } from "./type";
import { dummyRoomInputZodSchema, type DummyRoomInputZodSchemaType } from "./validation-schema";

import type { ProductDummyRoomFormModelContextType } from "../ui/_shared/context/context";

type Props = {
	inputModel: ProductDummyRoomFormModel["InputModel"];
	queryData: ProductDummyRoomFormModel["QueryData"];
};

export const useInitProductDummyRoomFormModelContext = ({
	inputModel: _inputModel,
}: Props): Omit<ProductDummyRoomFormModelContextType, "queryData"> & { resetViewModel: () => void } => {
	const hotelID = _inputModel.hotelID;
	const roomID = _inputModel.roomID;

	const defaultInputModel = DEFAULT_PRODUCT_DUMMY_ROOM_INPUT_MODEL({ roomID, hotelID });
	const inputModel = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			ProductDummyRoomFormModel["InputModel"],
			ExcludeKeys,
			ProductDummyRoomFormModel["QueryData"],
			DummyRoomInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: inputModel,
			currentInput: inputModel,
			excludeKeys: excludeKeys,
			zodSchema: dummyRoomInputZodSchema,
		}),
	);

	const onSetViewModel: ProductDummyRoomFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as ProductDummyRoomFormModel["ViewModel"],
				),
			} satisfies ProductDummyRoomFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				ProductDummyRoomFormModel["InputModel"],
				ExcludeKeys,
				ProductDummyRoomFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				ProductDummyRoomFormModel["InputModel"],
				ExcludeKeys,
				ProductDummyRoomFormModel["QueryData"],
				DummyRoomInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: inputModel,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: dummyRoomInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, inputModel, viewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		viewModel,
		onSetViewModel,

		resetViewModel,
	};
};
